import { SocialPost } from '@lib/social/scheduling/posts';
import { calendarGrid } from '@lib/social/scheduling/window';
import { ActionButton } from '@pages/ActionButton';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { socialScheduleStore } from '@stores/SocialSchedule';
import classNames from 'classnames';
import { format, isSameDay, isSameMonth, startOfDay } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import pageCss from '../../SchedulePage.module.scss';
import { PostChip } from '../PostChip/PostChip';
import { PostListPopover } from '../PostListPopover/PostListPopover';
import css from './ScheduleCalendar.module.scss';

/** Chips shown before a cell folds the rest into "+N more". */
const CHIPS_PER_CELL = 3;
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The month's six-row grid. The page owns the month and the request; this reads both. */
export const ScheduleCalendar = observer(function ScheduleCalendar({
  month,
  now,
}: {
  month: Date;
  now: Date;
}) {
  // The day whose "+N more" is open, anchored to its cell so the popout sits over it.
  const [openDay, setOpenDay] = useState<{ key: number; anchor: HTMLElement } | null>(null);
  const grid = calendarGrid(month);

  const state = socialScheduleStore.rangeState(grid.range);
  const postsByDay = new Map<number, SocialPost[]>();
  for (const post of socialScheduleStore.postsBetween(grid.range)) {
    const day = startOfDay(scheduleSettings.inZone(post.publishAt)).getTime();
    const list = postsByDay.get(day);
    if (list) list.push(post);
    else postsByDay.set(day, [post]);
  }

  return (
    <div className={css.calendar}>
      {state?.error && !state.loaded ? (
        <div className={pageCss.stateRow}>
          <span>Something went wrong. Try again later.</span>
          <ActionButton
            variant="secondary"
            onClick={() => void socialScheduleStore.load(grid.range)}
          >
            Retry
          </ActionButton>
        </div>
      ) : (
        // The grid stays in place while its month loads, dimmed: the loading
        // state lives in the view, not in the header, so nothing shifts.
        <div
          className={classNames(css.grid, !state?.loaded && css.loading)}
          aria-busy={!state?.loaded}
        >
          {WEEKDAYS.map((weekday) => (
            <div key={weekday} className={css.weekday}>
              {weekday}
            </div>
          ))}
          {grid.days.map((day) => {
            const dayKey = day.getTime();
            const posts = postsByDay.get(dayKey) ?? [];
            const shown = posts.slice(0, CHIPS_PER_CELL);
            const hidden = posts.length - shown.length;
            // The whole cell opens the day's popout when it has posts; the
            // "+N more" control is the same action with a visible handle.
            const open = (cell: HTMLElement) => setOpenDay({ key: dayKey, anchor: cell });
            return (
              <div
                key={dayKey}
                className={classNames(
                  css.cell,
                  posts.length > 0 && css.cellWithPosts,
                  !isSameMonth(day, month) && css.outsideMonth,
                  isSameDay(day, now) && css.today,
                )}
                role={posts.length > 0 ? 'button' : undefined}
                tabIndex={posts.length > 0 ? 0 : undefined}
                aria-label={
                  posts.length > 0
                    ? `${format(day, 'MMMM d')}, ${posts.length} ${posts.length === 1 ? 'post' : 'posts'}`
                    : undefined
                }
                onClick={(e) => posts.length > 0 && open(e.currentTarget)}
                onKeyDown={(e) => {
                  if (posts.length > 0 && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    open(e.currentTarget);
                  }
                }}
              >
                <span className={css.dayNumber}>{format(day, 'd')}</span>
                {shown.map((post) => (
                  <PostChip key={post.id} post={post} />
                ))}
                {hidden > 0 && <span className={css.more}>+{hidden} more</span>}
              </div>
            );
          })}
        </div>
      )}

      <PostListPopover
        anchorEl={openDay?.anchor ?? null}
        onClose={() => setOpenDay(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        header={
          openDay && (
            <div className={css.dayPopoverDate}>
              <span className={css.dayPopoverWeekday}>
                {format(scheduleSettings.inZone(openDay.key), 'EEE')}
              </span>
              <span className={css.dayPopoverNumber}>
                {format(scheduleSettings.inZone(openDay.key), 'd')}
              </span>
            </div>
          )
        }
      >
        {openDay &&
          (postsByDay.get(openDay.key) ?? []).map((post) => (
            <PostChip key={post.id} post={post} link="same-tab" />
          ))}
      </PostListPopover>
    </div>
  );
});
